import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi, useToaster } from '../../../utils/hooks';
import { useNavigationGuard } from '../../../utils/hooks/useNavigationGuard';
import { Helpers } from '../../../utils/helpers';
import {
  IClubMemberAddRequest,
  IClubMemberAddResult,
  IClubMemberDeleteResult,
  IClubMemberUpdateResult,
  IClubMemberDto,
  IFamilyMemberDto,
  IMembershipTypeLookupDto,
  ISystemCodeByTypeDto,
  ISecondaryFamilyMemberUpdateRequest,
  ISecondaryFamilyMemberUpdateResult,
  IFamilyMemberChildDto,
  IFamilyMemberChildResult,
  IUserDto,
  IClubMemberApiSearchRequest
} from '../../../utils/constants/interfaces';
import { IClubMemberLookupUiState } from '../../../components/member/ClubMemberLookup/ClubMemberLookup';
import { ToastStyle } from '../../../utils/hooks/useToaster';
import {
  FamilyMemberType,
  MemberStatus,
  MemberTitle,
  MemberSuffix
} from '../../../utils/constants/enums';

type ClubMemberKey = keyof IClubMemberDto;
type FamilyMemberKey = keyof IFamilyMemberDto;

// Track what action triggered the unsaved changes dialog
type DialogAction = 'back' | 'cancel' | 'tabSwitch' | 'navigation' | null;

// Define the shape of the location state
interface LocationState {
  prefill?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
  };
  // Search parameters from the member lookup page (for returning back)
  searchParams?: IClubMemberApiSearchRequest;
  uiState?: IClubMemberLookupUiState;
}

/**
 * Helper function to safely handle date conversions between UI and API.
 * For date-only fields, we ensure the date remains the same regardless of timezone.
 */
const handleDateConversion = (dateInput: string | Date | null): Date | null => {
  if (!dateInput) return null;

  let result: Date;

  if (typeof dateInput === 'string') {
    if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      result = new Date(year, month - 1, day, 12, 0, 0, 0); // Use noon to avoid timezone shifts
    } else {
      const parsedDate = new Date(dateInput);
      result = new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate(),
        12,
        0,
        0,
        0
      );
    }
  } else {
    // If it's already a Date object, extract date parts and recreate with noon time
    result = new Date(
      dateInput.getFullYear(),
      dateInput.getMonth(),
      dateInput.getDate(),
      12,
      0,
      0,
      0
    );
  }

  // Return null if invalid date
  return isNaN(result.getTime()) ? null : result;
};

const defaultClubMember: IClubMemberDto = {
  id: '',
  notes: '',
  primaryMemberId: 0,
  secondaryMemberId: 0,
  membershipTypeId: 0,
  membershipType: {
    id: 0,
    name: '',
    categoryCodeId: 0,
    classCodeId: 0,
    initiationFee: 0,
    dues: 0,
    description: '',
    sortOrder: 0,
    familyMembers: false,
    createdBy: '',
    dateCreatedUtc: new Date(),
    lastModifiedBy: '',
    lastModifiedDateUtc: new Date(),
    categoryCode: {
      id: 0,
      codeType: '',
      codeValue: '',
      description: '',
      info: '',
      active: false,
      sortOrder: 0,
      createdBy: '',
      dateCreatedUtc: new Date(),
      lastModifiedBy: '',
      lastModifiedDateUtc: new Date()
    },
    classCode: {
      id: 0,
      codeType: '',
      codeValue: '',
      description: '',
      info: '',
      active: false,
      sortOrder: 0,
      createdBy: '',
      dateCreatedUtc: new Date(),
      lastModifiedBy: '',
      lastModifiedDateUtc: new Date()
    }
  },
  createdBy: '',
  dateCreatedUtc: new Date(),
  lastModifiedBy: '',
  lastModifiedDateUtc: new Date(),
  familyMembers: [],
  primaryMember: {
    id: '',
    legacyMemberId: '',
    firstName: '',
    lastName: '',
    middleName: '',
    title: null,
    suffix: null,
    status: MemberStatus.NewCandidate,
    memberStatusId: 0,
    memberStatus: {
      id: 0,
      codeType: 'MEMBER_STATUS',
      codeValue: '',
      description: '',
      info: '',
      active: true,
      sortOrder: 0,
      createdBy: '',
      dateCreatedUtc: new Date(),
      lastModifiedBy: '',
      lastModifiedDateUtc: new Date()
    },
    relationship: '',
    memberType: FamilyMemberType.Adult,
    emailAddress: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    profession: '',
    isNraMember: false,
    nraNumber: '',
    dateOfBirthUtc: null,
    regularMemberDateUtc: null,
    honoraryMemberDateUtc: null,
    seniorMemberDateUtc: null,
    lifeMemberDateUtc: null,
    firstJoinedDateUtc: null,
    joinedDateUtc: null,
    membershipTypeId: null,
    membershipTypeName: '',
    clubMemberId: 0,
    isPrimaryMember: true,
    applicationUserId: '',
    createdBy: '',
    dateCreatedUtc: new Date(),
    lastModifiedBy: '',
    lastModifiedDateUtc: null,
    isDeleted: false,
    interests: []
  },
  secondaryMember: null
};

const useClubMemberEditor = (clubMemberId: string | null) => {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const toaster = useToaster();

  // Safely extract prefill data, search params, and UI state with proper type casting
  const state = location.state as LocationState | null;
  const prefillData = state?.prefill;
  // Search params and UI state from the member lookup page - to be passed back when returning
  const savedSearchParams = state?.searchParams;
  const savedUiState = state?.uiState;

  // Function to create a timezone-safe date from a date string (same as DateInput component)
  const createLocalDate = (dateString: string): Date | null => {
    if (!dateString) {
      return null;
    }

    // For YYYY-MM-DD format from HTML date input
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);

      // Additional validation to ensure the date parts are valid
      if (year < 1000 || month <= 0 || month > 12 || day <= 0 || day > 31) {
        return null;
      }

      const date = new Date();
      date.setFullYear(year);
      date.setMonth(month - 1); // Month is 0-based
      date.setDate(day);
      date.setHours(12, 0, 0, 0); // Set to noon (12:00) instead of midnight to avoid timezone issues
      return date;
    }

    return null;
  };

  const [clubMember, setClubMember] = useState<IClubMemberDto>(() => {
    // If prefill data exists, apply it to the default club member
    if (prefillData) {
      return {
        ...defaultClubMember,
        primaryMember: {
          ...defaultClubMember.primaryMember,
          firstName: prefillData.firstName || '',
          lastName: prefillData.lastName || '',
          dateOfBirthUtc: prefillData.dateOfBirth ? createLocalDate(prefillData.dateOfBirth) : null
        }
      };
    }
    return defaultClubMember;
  });

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [loadingClubMember, setLoadingClubMember] = useState<boolean>(true);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(!!prefillData); // Set dirty if prefilled
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState<boolean>(false);
  const [dialogAction, setDialogAction] = useState<DialogAction>(null);
  const [targetTab, setTargetTab] = useState<string>('main');
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string>('');
  const [membershipTypes, setMembershipTypes] = useState<IMembershipTypeLookupDto[]>([]);
  const [loadingMembershipTypes, setLoadingMembershipTypes] = useState<boolean>(false);
  const [interests, setInterests] = useState<ISystemCodeByTypeDto[]>([]);
  const [loadingInterests, setLoadingInterests] = useState<boolean>(false);
  const [selectedInterests, setSelectedInterests] = useState<number[]>([]);
  const [savingSecondaryMember, setSavingSecondaryMember] = useState<boolean>(false);

  // New state for member status options
  const [memberStatusOptions, setMemberStatusOptions] = useState<ISystemCodeByTypeDto[]>([]);
  const [loadingMemberStatuses, setLoadingMemberStatuses] = useState<boolean>(false);

  // Keep a cached copy of the secondary member for cancellation without reloading from server
  const [originalSecondaryMember, setOriginalSecondaryMember] = useState<IFamilyMemberDto | null>(
    null
  );

  // State for family member children
  const [familyMemberChildren, setFamilyMemberChildren] = useState<IFamilyMemberChildDto[]>([]);
  const [loadingChildren, setLoadingChildren] = useState<boolean>(false);

  // Committees (moved to ClubMemberEditor level for WorkHoursSection)
  const [committees, setCommittees] = useState<ISystemCodeByTypeDto[]>([]);
  const [loadingCommittees, setLoadingCommittees] = useState<boolean>(false);

  // Send first time email state
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);

  // Set password modal state (primary member)
  const [showSetPasswordModal, setShowSetPasswordModal] = useState<boolean>(false);

  const openSetPasswordModal = () => {
    setShowSetPasswordModal(true);
  };

  const closeSetPasswordModal = () => {
    setShowSetPasswordModal(false);
  };

  const onPasswordSetSuccess = () => {
    setShowSetPasswordModal(false);
    toaster.addToast('Password set successfully!', ToastStyle.SUCCESS, 3000);
  };

  // Set password modal state (secondary member)
  const [showSecondarySetPasswordModal, setShowSecondarySetPasswordModal] =
    useState<boolean>(false);

  // Email account reuse confirmation dialog state
  const [showEmailReuseConfirmDialog, setShowEmailReuseConfirmDialog] = useState<boolean>(false);
  const [emailConflictInfo, setEmailConflictInfo] = useState<{
    email: string;
    memberId: number;
    memberName: string;
    memberStatus: string;
    relationship: string;
    message: string;
  } | null>(null);
  const [pendingSecondaryMemberSave, setPendingSecondaryMemberSave] =
    useState<ISecondaryFamilyMemberUpdateRequest | null>(null);
  const [pendingClubMemberSave, setPendingClubMemberSave] = useState<IClubMemberAddRequest | null>(
    null
  );
  const [pendingClubMemberUpdate, setPendingClubMemberUpdate] = useState<IClubMemberDto | null>(
    null
  );

  const openSecondarySetPasswordModal = () => {
    setShowSecondarySetPasswordModal(true);
  };

  const closeSecondarySetPasswordModal = () => {
    setShowSecondarySetPasswordModal(false);
  };

  const onSecondaryPasswordSetSuccess = () => {
    setShowSecondarySetPasswordModal(false);
    toaster.addToast('Secondary member password set successfully!', ToastStyle.SUCCESS, 3000);
  };

  // Delete member confirmation modal state
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);

  const openDeleteConfirmationModal = () => {
    setShowDeleteConfirmationModal(true);
  };

  const closeDeleteConfirmationModal = () => {
    setShowDeleteConfirmationModal(false);
  };

  // Add child functionality
  const [showAddChildModal, setShowAddChildModal] = useState<boolean>(false);
  const [addingChild, setAddingChild] = useState<boolean>(false);
  const [childFormData, setChildFormData] = useState<{
    firstName: string;
    lastName: string;
    middleName: string;
    dateOfBirthUtc: Date | null;
  }>({
    firstName: '',
    lastName: '',
    middleName: '',
    dateOfBirthUtc: null
  });
  const [childFormErrors, setChildFormErrors] = useState<string[]>([]);

  // New state for editing children
  const [childFormMode, setChildFormMode] = useState<'add' | 'edit'>('add');
  const [editingChildId, setEditingChildId] = useState<number | null>(null);
  const [showDeleteChildConfirmation, setShowDeleteChildConfirmation] = useState<boolean>(false);
  const [deletingChild, setDeletingChild] = useState<boolean>(false);

  // Browser navigation protection - warn on page refresh/close if dirty
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Navigation guard to intercept sidebar/link navigation
  const handleNavigationAttempt = useCallback((targetPath: string) => {
    setPendingNavigationPath(targetPath);
    setDialogAction('navigation');
    setShowUnsavedChangesDialog(true);
  }, []);

  useNavigationGuard({
    isDirty,
    onNavigationAttempt: handleNavigationAttempt
  });

  // Load membership types on component mount
  useEffect(() => {
    const loadMembershipTypes = async () => {
      setLoadingMembershipTypes(true);
      try {
        const response = await api.get<IMembershipTypeLookupDto[]>('/membership-types/lookup');
        if (response?.status?.statusCode === 200 && response.response) {
          setMembershipTypes(response.response);
        } else {
          toaster.addToast('Error loading membership types', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        toaster.addToast('Error loading membership types', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingMembershipTypes(false);
      }
    };

    void loadMembershipTypes();
    // We want this to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load member status options on component mount
  useEffect(() => {
    const loadMemberStatuses = async () => {
      setLoadingMemberStatuses(true);
      try {
        const response = await api.get<ISystemCodeByTypeDto[]>(
          '/codes/code-types-by-type/MEMBER_STATUS'
        );
        if (response?.status?.statusCode === 200 && response.response) {
          setMemberStatusOptions(response.response);
        } else {
          toaster.addToast('Error loading member statuses', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        toaster.addToast('Error loading member statuses', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingMemberStatuses(false);
      }
    };

    void loadMemberStatuses();
    // We want this to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load interests on component mount
  useEffect(() => {
    const loadInterests = async () => {
      setLoadingInterests(true);
      try {
        const response = await api.get<ISystemCodeByTypeDto[]>(
          '/codes/code-types-by-type/INTEREST'
        );
        if (response?.status?.statusCode === 200 && response.response) {
          setInterests(response.response);
        } else {
          toaster.addToast('Error loading interests', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        toaster.addToast('Error loading interests', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingInterests(false);
      }
    };

    void loadInterests();
    // We specifically want this to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update selected interests when member data changes
  useEffect(() => {
    if (clubMember.primaryMember.interests && clubMember.primaryMember.interests.length > 0) {
      const interestIds = clubMember.primaryMember.interests.map(
        (interest) => interest.systemCodeId
      );
      setSelectedInterests(interestIds);
    } else {
      setSelectedInterests([]);
    }
  }, [clubMember.primaryMember.interests]);

  // Fetch children from the API endpoint
  const fetchFamilyMemberChildren = useCallback(
    async (clubMemberId: string): Promise<void> => {
      if (!clubMemberId || clubMemberId === 'new') {
        setFamilyMemberChildren([]);
        return;
      }

      setLoadingChildren(true);

      try {
        const endpoint = `/members/${clubMemberId}/children`;
        const apiResponse = await api.get<IFamilyMemberChildDto[]>(endpoint);

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
          setFamilyMemberChildren(apiResponse.response);
        } else {
          setFamilyMemberChildren([]);
          toaster.addToast('Error fetching family member children', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        setFamilyMemberChildren([]);
        toaster.addToast('Error fetching family member children', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingChildren(false);
      }
    },
    [api, toaster]
  );

  // Load children when club member ID changes
  useEffect(() => {
    if (clubMember.id && clubMember.id !== 'new') {
      void fetchFamilyMemberChildren(clubMember.id);
    } else {
      setFamilyMemberChildren([]);
    }
    // fetchFamilyMemberChildren is stable but depends on api/toaster which may change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubMember.id]);

  // Load committees on component mount
  useEffect(() => {
    const loadCommittees = async () => {
      setLoadingCommittees(true);
      try {
        const response = await api.get<ISystemCodeByTypeDto[]>(
          '/codes/code-types-by-type/COMMITTEE'
        );
        if (response?.status?.statusCode === 200 && response.response) {
          setCommittees(response.response);
        } else {
          toaster.addToast('Error loading committees', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        toaster.addToast('Error loading committees', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingCommittees(false);
      }
    };

    void loadCommittees();
    // We want this to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close unsaved changes dialog
  const closeUnsavedChangesDialog = () => {
    setShowUnsavedChangesDialog(false);
    setDialogAction(null);
    setPendingNavigationPath(''); // Clear pending navigation
  };

  const discardUnsavedChanges = () => {
    setShowUnsavedChangesDialog(false);
    setIsDirty(false);

    if (dialogAction === 'back') {
      // Pass search params and UI state back to the member search page
      void navigate('/members', {
        state:
          savedSearchParams || savedUiState
            ? { searchParams: savedSearchParams, uiState: savedUiState }
            : undefined
      });
    } else if (dialogAction === 'cancel') {
      if (Helpers.isNullOrWhiteSpace(clubMember.id) || clubMember.id === 'new') {
        setClubMember(defaultClubMember);
      } else {
        void loadClubMember(clubMemberId);
      }
      setFieldErrors([]);
    } else if (dialogAction === 'tabSwitch') {
      // Discard changes and allow tab switch
      if (Helpers.isNullOrWhiteSpace(clubMember.id) || clubMember.id === 'new') {
        setClubMember(defaultClubMember);
      } else {
        void loadClubMember(clubMemberId);
      }
      setFieldErrors([]);
    } else if (dialogAction === 'navigation') {
      // Discard changes and proceed with navigation
      if (Helpers.isNullOrWhiteSpace(clubMember.id) || clubMember.id === 'new') {
        setClubMember(defaultClubMember);
      } else {
        void loadClubMember(clubMemberId);
      }
      setFieldErrors([]);
      // Navigate to the pending path
      if (pendingNavigationPath) {
        void navigate(pendingNavigationPath);
        setPendingNavigationPath('');
      }
    }

    setDialogAction(null);
  };

  const back = () => {
    if (isDirty) {
      setDialogAction('back');
      setShowUnsavedChangesDialog(true);
      return;
    }
    setClubMember(defaultClubMember);
    // Pass search params and UI state back to the member search page
    void navigate('/members', {
      state:
        savedSearchParams || savedUiState
          ? { searchParams: savedSearchParams, uiState: savedUiState }
          : undefined
    });
  };

  const cancelChanges = () => {
    if (isDirty) {
      setDialogAction('cancel');
      setShowUnsavedChangesDialog(true);
      return;
    }

    // No changes to discard, just reset any possible field errors
    setFieldErrors([]);
  };

  const loadClubMember = useCallback(
    async (clubMemberId: string | null): Promise<void> => {
      if (!Helpers.isNullOrWhiteSpace(clubMemberId)) {
        if (clubMemberId === 'new') {
          // For new members, keep the existing state which may include prefilled data
          if (!state?.prefill) {
            setClubMember(defaultClubMember);
          }
          setIsDirty(!!state?.prefill);
        } else {
          setFieldErrors([]);
          setLoadingClubMember(true);
          const endpoint = `/members/${clubMemberId}`;
          const apiResponse = await api.get<IClubMemberDto>(endpoint);
          if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
            // Process the response data to properly handle dates
            const clubMemberData = apiResponse.response;

            // Process primary member dates
            if (clubMemberData.primaryMember) {
              clubMemberData.primaryMember.dateOfBirthUtc = handleDateConversion(
                clubMemberData.primaryMember.dateOfBirthUtc
              );
              clubMemberData.primaryMember.firstJoinedDateUtc = handleDateConversion(
                clubMemberData.primaryMember.firstJoinedDateUtc
              );
              clubMemberData.primaryMember.joinedDateUtc = handleDateConversion(
                clubMemberData.primaryMember.joinedDateUtc
              );
              clubMemberData.primaryMember.regularMemberDateUtc = handleDateConversion(
                clubMemberData.primaryMember.regularMemberDateUtc
              );
              clubMemberData.primaryMember.honoraryMemberDateUtc = handleDateConversion(
                clubMemberData.primaryMember.honoraryMemberDateUtc
              );
              clubMemberData.primaryMember.seniorMemberDateUtc = handleDateConversion(
                clubMemberData.primaryMember.seniorMemberDateUtc
              );
              clubMemberData.primaryMember.lifeMemberDateUtc = handleDateConversion(
                clubMemberData.primaryMember.lifeMemberDateUtc
              );

              // Non-date fields that must remain Date objects
              if (typeof clubMemberData.primaryMember.dateCreatedUtc === 'string') {
                clubMemberData.primaryMember.dateCreatedUtc = new Date(
                  clubMemberData.primaryMember.dateCreatedUtc
                );
              }
              if (typeof clubMemberData.primaryMember.lastModifiedDateUtc === 'string') {
                clubMemberData.primaryMember.lastModifiedDateUtc = new Date(
                  clubMemberData.primaryMember.lastModifiedDateUtc
                );
              }
            }

            // Process family members dates
            if (clubMemberData.familyMembers && clubMemberData.familyMembers.length > 0) {
              clubMemberData.familyMembers = clubMemberData.familyMembers.map((member) => {
                const updatedMember = { ...member };
                // Process date fields
                updatedMember.dateOfBirthUtc = handleDateConversion(member.dateOfBirthUtc);
                updatedMember.firstJoinedDateUtc = handleDateConversion(member.firstJoinedDateUtc);
                updatedMember.joinedDateUtc = handleDateConversion(member.joinedDateUtc);
                updatedMember.regularMemberDateUtc = handleDateConversion(
                  member.regularMemberDateUtc
                );
                updatedMember.honoraryMemberDateUtc = handleDateConversion(
                  member.honoraryMemberDateUtc
                );
                updatedMember.seniorMemberDateUtc = handleDateConversion(
                  member.seniorMemberDateUtc
                );
                updatedMember.lifeMemberDateUtc = handleDateConversion(member.lifeMemberDateUtc);

                // Non-date fields that must remain Date objects
                if (typeof member.dateCreatedUtc === 'string') {
                  updatedMember.dateCreatedUtc = new Date(member.dateCreatedUtc);
                }
                if (typeof member.lastModifiedDateUtc === 'string') {
                  updatedMember.lastModifiedDateUtc = member.lastModifiedDateUtc
                    ? new Date(member.lastModifiedDateUtc)
                    : null;
                }

                return updatedMember;
              });
            }

            // Process club member dates - these are audit dates
            if (typeof clubMemberData.dateCreatedUtc === 'string') {
              clubMemberData.dateCreatedUtc = new Date(clubMemberData.dateCreatedUtc);
            }
            if (typeof clubMemberData.lastModifiedDateUtc === 'string') {
              clubMemberData.lastModifiedDateUtc = new Date(clubMemberData.lastModifiedDateUtc);
            }

            // Process secondary member dates (if exists)
            if (clubMemberData.secondaryMember) {
              clubMemberData.secondaryMember.dateOfBirthUtc = handleDateConversion(
                clubMemberData.secondaryMember.dateOfBirthUtc
              );
              clubMemberData.secondaryMember.firstJoinedDateUtc = handleDateConversion(
                clubMemberData.secondaryMember.firstJoinedDateUtc
              );
              clubMemberData.secondaryMember.joinedDateUtc = handleDateConversion(
                clubMemberData.secondaryMember.joinedDateUtc
              );
              clubMemberData.secondaryMember.regularMemberDateUtc = handleDateConversion(
                clubMemberData.secondaryMember.regularMemberDateUtc
              );
              clubMemberData.secondaryMember.honoraryMemberDateUtc = handleDateConversion(
                clubMemberData.secondaryMember.honoraryMemberDateUtc
              );
              clubMemberData.secondaryMember.seniorMemberDateUtc = handleDateConversion(
                clubMemberData.secondaryMember.seniorMemberDateUtc
              );
              clubMemberData.secondaryMember.lifeMemberDateUtc = handleDateConversion(
                clubMemberData.secondaryMember.lifeMemberDateUtc
              );

              // Non-date fields that must remain Date objects
              if (typeof clubMemberData.secondaryMember.dateCreatedUtc === 'string') {
                clubMemberData.secondaryMember.dateCreatedUtc = new Date(
                  clubMemberData.secondaryMember.dateCreatedUtc
                );
              }
              if (typeof clubMemberData.secondaryMember.lastModifiedDateUtc === 'string') {
                clubMemberData.secondaryMember.lastModifiedDateUtc = clubMemberData.secondaryMember
                  .lastModifiedDateUtc
                  ? new Date(clubMemberData.secondaryMember.lastModifiedDateUtc)
                  : null;
              }
            }

            setClubMember(clubMemberData);

            // Update the originalSecondaryMember cache when loading from server
            if (clubMemberData.secondaryMember) {
              setOriginalSecondaryMember(
                JSON.parse(JSON.stringify(clubMemberData.secondaryMember)) as IFamilyMemberDto
              );
            } else {
              setOriginalSecondaryMember(null);
            }
          } else {
            await navigate('/members?error+loading+member');
          }
        }
        setLoadingClubMember(false);
      }
    },
    [api, navigate, state]
  );

  const updateClubMemberValue = <K extends ClubMemberKey>(
    key: K,
    value: IClubMemberDto[K]
  ): void => {
    if (isSaving || !clubMember) return;
    setIsDirty(true);

    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    setClubMember((current) => ({
      ...current,
      [key]: value
    }));
  };

  const updatePrimaryMemberValue = <K extends FamilyMemberKey>(
    key: K,
    value: IFamilyMemberDto[K]
  ): void => {
    if (isSaving || !clubMember) return;
    setIsDirty(true);

    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    setClubMember((current) => ({
      ...current,
      primaryMember: {
        ...current.primaryMember,
        [key]: value
      }
    }));
  };

  const validate = () => {
    const errors: string[] = [];
    const isNew = Helpers.isNullOrWhiteSpace(clubMember.id) || clubMember.id === 'new';

    if (Helpers.isNullOrWhiteSpace(clubMember.primaryMember.firstName)) {
      errors.push('overview:firstName');
    }
    if (Helpers.isNullOrWhiteSpace(clubMember.primaryMember.lastName)) {
      errors.push('overview:lastName');
    }
    if (clubMember.membershipTypeId == null || clubMember.membershipTypeId === 0) {
      errors.push('overview:membershipTypeId');
    }
    // Status is required for both new and existing members
    if (
      clubMember.primaryMember.memberStatusId == null ||
      clubMember.primaryMember.memberStatusId === 0
    ) {
      errors.push('overview:memberStatusId');
    }
    if (isNew && !clubMember.primaryMember.dateOfBirthUtc) {
      errors.push('overview:dateOfBirthUtc');
    }
    if (!isNew) {
      if (!clubMember.primaryMember.firstJoinedDateUtc) {
        errors.push('overview:firstJoinedDateUtc');
      }
      if (!clubMember.primaryMember.joinedDateUtc) {
        errors.push('overview:joinedDateUtc');
      }
    }
    setFieldErrors(errors);
    return errors.length === 0;
  };

  const emptyClubMemberReplacer = (
    key: string,
    value: string | string[] | boolean | null | Date
  ) => {
    if (
      key === 'id' &&
      ['string', 'null', 'undefined'].includes(typeof value) &&
      (Helpers.isNullOrWhiteSpace(value as string | null | undefined) || value === 'new')
    ) {
      return undefined; // Exclude this property from the JSON output
    }
    // Don't convert null titles and suffixes to 0 anymore since the API expects them as is
    return value; // Keep all other properties as is
  };

  const handleInterestChange = (interestId: number, checked: boolean) => {
    if (isSaving) return;
    setIsDirty(true);

    setSelectedInterests((prev) => {
      if (checked) {
        return [...prev, interestId];
      } else {
        return prev.filter((id) => id !== interestId);
      }
    });
  };

  const saveChanges = async (): Promise<void> => {
    if (isSaving) return;

    const isValid = validate();
    if (!isValid) {
      toaster.addToast(`Unable to save — required fields are missing.`, ToastStyle.ERROR, 5000);
      Helpers.scrollToClassName('is-invalid', -20);
      return;
    }

    setIsSaving(true);

    const isNew = Helpers.isNullOrWhiteSpace(clubMember.id) || clubMember.id === 'new';

    if (isNew) {
      // Create a new club member using IClubMemberAddRequest
      const addRequest: IClubMemberAddRequest = {
        membershipTypeId: clubMember.membershipTypeId,
        notes: clubMember.notes || '',
        firstName: clubMember.primaryMember.firstName || '',
        lastName: clubMember.primaryMember.lastName || '',
        middleName: clubMember.primaryMember.middleName || '',
        // Use None/0 for unselected values
        title:
          clubMember.primaryMember.title !== null && clubMember.primaryMember.title !== undefined
            ? clubMember.primaryMember.title
            : (0 as MemberTitle), // Use 0 for None
        suffix:
          clubMember.primaryMember.suffix !== null && clubMember.primaryMember.suffix !== undefined
            ? clubMember.primaryMember.suffix
            : (0 as MemberSuffix), // Use 0 for None
        memberStatusId: clubMember.primaryMember.memberStatusId || 0,
        emailAddress: clubMember.primaryMember.emailAddress || '',
        address: clubMember.primaryMember.address || '',
        address2: clubMember.primaryMember.address2 || '',
        city: clubMember.primaryMember.city || '',
        state: clubMember.primaryMember.state || '',
        zipCode: clubMember.primaryMember.zipCode || '',
        homePhone: clubMember.primaryMember.homePhone || '',
        cellPhone: clubMember.primaryMember.cellPhone || '',
        profession: clubMember.primaryMember.profession || '',
        isNraMember: clubMember.primaryMember.isNraMember,
        nraNumber: clubMember.primaryMember.nraNumber || '',
        // Handle date conversion - validation will catch if missing for new members
        dateOfBirth:
          handleDateConversion(clubMember.primaryMember.dateOfBirthUtc) ?? new Date(1900, 0, 1), // Fallback for TS - validation should prevent this from being used
        firstJoinedDateUtc: handleDateConversion(clubMember.primaryMember.firstJoinedDateUtc),
        joinedDateUtc: handleDateConversion(clubMember.primaryMember.joinedDateUtc),
        regularMemberDateUtc: handleDateConversion(clubMember.primaryMember.regularMemberDateUtc),
        honoraryMemberDateUtc: handleDateConversion(clubMember.primaryMember.honoraryMemberDateUtc),
        seniorMemberDateUtc: handleDateConversion(clubMember.primaryMember.seniorMemberDateUtc),
        lifeMemberDateUtc: handleDateConversion(clubMember.primaryMember.lifeMemberDateUtc),
        interestIds: selectedInterests,
        sendWelcomeEmail: true, // Hardcoded for now
        createdByUsername: 'currentUser' // Replace with actual username from auth context
      };

      const endpoint = `/members`;
      const apiResponse = await api.post<IClubMemberAddResult>(
        endpoint,
        JSON.stringify(addRequest)
      );

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
        setIsDirty(false);
        toaster.addToast(`Member created successfully!`, ToastStyle.SUCCESS, 2000);
        await navigate(`/members/${apiResponse.response.clubMemberId}`);
      } else if (apiResponse?.response?.requiresAccountReuseConfirmation) {
        // Email conflict detected - need user confirmation
        setEmailConflictInfo({
          email: apiResponse.response.conflictingEmail || '',
          memberId: apiResponse.response.conflictingMemberId || 0,
          memberName: apiResponse.response.conflictingMemberName || '',
          memberStatus: apiResponse.response.conflictingMemberStatus || '',
          relationship: apiResponse.response.conflictingRelationship || '',
          message: apiResponse.response.message || ''
        });
        setPendingClubMemberSave(addRequest);
        setShowEmailReuseConfirmDialog(true);
      } else {
        toaster.addToast(
          `Error creating member: ${apiResponse.response?.message || 'Unknown error'}`,
          ToastStyle.ERROR,
          8000
        );
      }
    } else {
      // For existing members, update the interests in the clubMember object
      const updatedClubMember = {
        ...clubMember,
        primaryMember: {
          ...clubMember.primaryMember,
          interests: selectedInterests.map((id) => ({
            familyMemberId: Number(clubMember.primaryMember.id),
            systemCodeId: id
          }))
        }
      };

      // Edit existing club member using IClubMemberDto
      const saveRequest = JSON.stringify(updatedClubMember, emptyClubMemberReplacer);
      const endpoint = `/members/${clubMember.id}`;
      const apiResponse = await api.put<IClubMemberUpdateResult>(endpoint, saveRequest);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
        setIsDirty(false);
        toaster.addToast(`Member updated successfully!`, ToastStyle.SUCCESS, 2000);

        // Process the response data after a successful save
        const memberData = apiResponse.response.clubMember;
        if (!memberData) return;

        // Process primary member dates
        if (memberData.primaryMember) {
          memberData.primaryMember.dateOfBirthUtc = handleDateConversion(
            memberData.primaryMember.dateOfBirthUtc
          );
          memberData.primaryMember.firstJoinedDateUtc = handleDateConversion(
            memberData.primaryMember.firstJoinedDateUtc
          );
          memberData.primaryMember.joinedDateUtc = handleDateConversion(
            memberData.primaryMember.joinedDateUtc
          );
          memberData.primaryMember.regularMemberDateUtc = handleDateConversion(
            memberData.primaryMember.regularMemberDateUtc
          );
          memberData.primaryMember.honoraryMemberDateUtc = handleDateConversion(
            memberData.primaryMember.honoraryMemberDateUtc
          );
          memberData.primaryMember.seniorMemberDateUtc = handleDateConversion(
            memberData.primaryMember.seniorMemberDateUtc
          );
          memberData.primaryMember.lifeMemberDateUtc = handleDateConversion(
            memberData.primaryMember.lifeMemberDateUtc
          );

          // Non-date fields that must remain Date objects
          memberData.primaryMember.dateCreatedUtc = new Date(
            memberData.primaryMember.dateCreatedUtc
          );
          memberData.primaryMember.lastModifiedDateUtc = memberData.primaryMember
            .lastModifiedDateUtc
            ? new Date(memberData.primaryMember.lastModifiedDateUtc)
            : null;
        }

        // Process family members dates
        if (memberData.familyMembers && memberData.familyMembers.length > 0) {
          memberData.familyMembers = memberData.familyMembers.map((member: IFamilyMemberDto) => {
            const updatedMember = { ...member };
            // Process date fields
            updatedMember.dateOfBirthUtc = handleDateConversion(member.dateOfBirthUtc);
            updatedMember.firstJoinedDateUtc = handleDateConversion(member.firstJoinedDateUtc);
            updatedMember.joinedDateUtc = handleDateConversion(member.joinedDateUtc);
            updatedMember.regularMemberDateUtc = handleDateConversion(member.regularMemberDateUtc);
            updatedMember.honoraryMemberDateUtc = handleDateConversion(
              member.honoraryMemberDateUtc
            );
            updatedMember.seniorMemberDateUtc = handleDateConversion(member.seniorMemberDateUtc);
            updatedMember.lifeMemberDateUtc = handleDateConversion(member.lifeMemberDateUtc);

            // Non-date fields that must remain Date objects
            updatedMember.dateCreatedUtc = new Date(member.dateCreatedUtc);
            updatedMember.lastModifiedDateUtc = member.lastModifiedDateUtc
              ? new Date(member.lastModifiedDateUtc)
              : null;

            return updatedMember;
          });
        }

        // Process club member dates - these are audit dates and must remain Date objects
        memberData.dateCreatedUtc = new Date(memberData.dateCreatedUtc);
        memberData.lastModifiedDateUtc = new Date(memberData.lastModifiedDateUtc);

        if (!memberData.secondaryMember && clubMember.secondaryMember) {
          memberData.secondaryMember = clubMember.secondaryMember;
        }

        setClubMember(memberData);
      } else if (apiResponse?.response?.requiresAccountReuseConfirmation) {
        // Email conflict detected - need user confirmation
        setEmailConflictInfo({
          email: apiResponse.response.conflictingEmail || '',
          memberId: apiResponse.response.conflictingMemberId || 0,
          memberName: apiResponse.response.conflictingMemberName || '',
          memberStatus: apiResponse.response.conflictingMemberStatus || '',
          relationship: apiResponse.response.conflictingRelationship || '',
          message: apiResponse.response.message || ''
        });
        setPendingClubMemberUpdate(updatedClubMember);
        setShowEmailReuseConfirmDialog(true);
      } else {
        toaster.addToast(
          `Error updating member: ${apiResponse.response?.message || 'Unknown error'}`,
          ToastStyle.ERROR,
          8000
        );
      }
    }

    setIsSaving(false);
  };

  const deleteMember = async (): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);
    const endpoint = `/members/${clubMember.id}`;
    const apiResponse = await api.delete<IClubMemberDeleteResult>(endpoint);
    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      if (apiResponse.response.succeeded) {
        toaster.addToast('Member status set to Delete.', ToastStyle.SUCCESS, 4000);
        await loadClubMember(clubMember.id);
      } else {
        toaster.addToast(apiResponse.response.message, ToastStyle.ERROR, 8000);
      }
    } else {
      toaster.addToast('Error deleting club member', ToastStyle.ERROR, 8000);
    }
    setIsSaving(false);
  };

  const sendFirstTimeEmail = async (): Promise<void> => {
    if (isSendingEmail || !clubMember.primaryMember.applicationUserId) return;
    setIsSendingEmail(true);
    const endpoint = `/users/${clubMember.primaryMember.applicationUserId}/first-time-email`;
    const apiResponse = await api.post<IUserDto>(endpoint, '');
    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      toaster.addToast('First time email sent successfully!', ToastStyle.SUCCESS, 3000);
    } else {
      toaster.addToast('Error sending first time email', ToastStyle.ERROR, 5000);
    }
    setIsSendingEmail(false);
  };

  // Function to save secondary family member
  const saveSecondaryFamilyMember = async (): Promise<void> => {
    if (savingSecondaryMember || !clubMember || !clubMember.id) return;

    // Basic validation for required fields
    const secondaryErrors: string[] = [];
    if (Helpers.isNullOrWhiteSpace(clubMember.secondaryMember?.firstName)) {
      secondaryErrors.push('family:firstName');
    }
    if (Helpers.isNullOrWhiteSpace(clubMember.secondaryMember?.lastName)) {
      secondaryErrors.push('family:lastName');
    }
    if (
      clubMember.secondaryMember?.memberStatusId == null ||
      clubMember.secondaryMember?.memberStatusId === 0
    ) {
      secondaryErrors.push('family:memberStatusId');
    }
    if (!clubMember.secondaryMember?.firstJoinedDateUtc) {
      secondaryErrors.push('family:firstJoinedDateUtc');
    }

    setFieldErrors(secondaryErrors);
    if (secondaryErrors.length > 0) {
      toaster.addToast(`Unable to save — required fields are missing.`, ToastStyle.ERROR, 5000);
      return;
    }

    setSavingSecondaryMember(true);

    // Create update request from secondary member data
    const updateRequest: ISecondaryFamilyMemberUpdateRequest = {
      clubMemberId: parseInt(clubMember.id),
      title: clubMember.secondaryMember?.title ?? MemberTitle.None,
      suffix: clubMember.secondaryMember?.suffix ?? MemberSuffix.None,
      firstName: clubMember.secondaryMember?.firstName || '',
      lastName: clubMember.secondaryMember?.lastName || '',
      middleName: clubMember.secondaryMember?.middleName || '',
      memberStatusId: clubMember.secondaryMember?.memberStatusId || 0,
      firstJoinedDateUtc:
        handleDateConversion(clubMember.secondaryMember?.firstJoinedDateUtc ?? null) ??
        new Date(1900, 0, 1), // Fallback for TS - validation should prevent this from being used
      profession: clubMember.secondaryMember?.profession || '',
      homePhone: clubMember.secondaryMember?.homePhone || '',
      cellPhone: clubMember.secondaryMember?.cellPhone || '',
      isNraMember: clubMember.secondaryMember?.isNraMember || false,
      nraNumber: clubMember.secondaryMember?.nraNumber || '',
      relationship: clubMember.secondaryMember?.relationship || '',
      emailAddress: clubMember.secondaryMember?.emailAddress || '',
      dateOfBirthUtc: handleDateConversion(clubMember.secondaryMember?.dateOfBirthUtc ?? null),
      address: clubMember.secondaryMember?.address || '',
      address2: clubMember.secondaryMember?.address2 || '',
      city: clubMember.secondaryMember?.city || '',
      state: clubMember.secondaryMember?.state || '',
      zipCode: clubMember.secondaryMember?.zipCode || ''
    };

    try {
      const endpoint = `/members/${clubMember.id}/family`;
      const apiResponse = await api.put<ISecondaryFamilyMemberUpdateResult>(
        endpoint,
        JSON.stringify(updateRequest)
      );

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
        toaster.addToast('Secondary family member saved successfully', ToastStyle.SUCCESS, 3000);

        // Reset dirty flag since changes have been saved successfully
        setIsDirty(false);

        // Clear any pending state
        setPendingSecondaryMemberSave(null);
        setEmailConflictInfo(null);

        // Reload the club member to get updated data
        await loadClubMember(clubMember.id);
      } else if (apiResponse?.response?.requiresAccountReuseConfirmation) {
        // Email conflict detected - need user confirmation
        setEmailConflictInfo({
          email: apiResponse.response.conflictingEmail || '',
          memberId: apiResponse.response.conflictingMemberId || 0,
          memberName: apiResponse.response.conflictingMemberName || '',
          memberStatus: apiResponse.response.conflictingMemberStatus || '',
          relationship: apiResponse.response.conflictingRelationship || '',
          message: apiResponse.response.message || ''
        });
        setPendingSecondaryMemberSave(updateRequest);
        setShowEmailReuseConfirmDialog(true);
      } else {
        toaster.addToast(
          `Error updating secondary member: ${apiResponse.response?.message || 'Unknown error'}`,
          ToastStyle.ERROR,
          5000
        );
      }
    } catch (error) {
      toaster.addToast('Error saving secondary family member', ToastStyle.ERROR, 5000);
    } finally {
      setSavingSecondaryMember(false);
    }
  };

  // Function to update secondary member values
  const updateSecondaryMemberValue = <K extends FamilyMemberKey>(
    key: K,
    value: IFamilyMemberDto[K]
  ): void => {
    if (isSaving || !clubMember) return;
    setIsDirty(true);

    // Remove any existing errors for this field
    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    // If secondaryMember is null, create a new object with default values
    const currentSecondaryMember = clubMember.secondaryMember || {
      id: '',
      legacyMemberId: '',
      firstName: '',
      lastName: '',
      middleName: '',
      title: null,
      suffix: null,
      status: MemberStatus.NewCandidate,
      memberStatusId: 0,
      memberStatus: {
        id: 0,
        codeType: 'MEMBER_STATUS',
        codeValue: '',
        description: '',
        info: '',
        active: true,
        sortOrder: 0,
        createdBy: '',
        dateCreatedUtc: new Date(),
        lastModifiedBy: '',
        lastModifiedDateUtc: new Date()
      },
      relationship: '',
      memberType: FamilyMemberType.Adult,
      emailAddress: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      homePhone: '',
      cellPhone: '',
      profession: '',
      isNraMember: false,
      nraNumber: '',
      dateOfBirthUtc: null,
      regularMemberDateUtc: null,
      honoraryMemberDateUtc: null,
      seniorMemberDateUtc: null,
      lifeMemberDateUtc: null,
      firstJoinedDateUtc: null,
      joinedDateUtc: null,
      membershipTypeId: null,
      membershipTypeName: '',
      clubMemberId: parseInt(clubMember.id),
      isPrimaryMember: false,
      applicationUserId: '',
      createdBy: '',
      dateCreatedUtc: new Date(),
      lastModifiedBy: '',
      lastModifiedDateUtc: null,
      isDeleted: false,
      interests: []
    };

    setClubMember((current) => ({
      ...current,
      secondaryMember: {
        ...currentSecondaryMember,
        [key]: value
      }
    }));
  };

  // Function to reset the secondary family member form
  const cancelSecondaryFamilyMember = () => {
    // If there's an existing secondary member, restore from cached copy
    // If not, set it to null
    if (Helpers.isNullOrWhiteSpace(clubMember.secondaryMember?.id)) {
      setClubMember((current) => ({
        ...current,
        secondaryMember: null
      }));
    } else {
      // Restore from our cached copy instead of reloading from server
      setClubMember((current) => ({
        ...current,
        secondaryMember: originalSecondaryMember
      }));
    }

    // Reset dirty flag since we've restored to the original state
    setIsDirty(false);

    // Clear any field errors related to the family form
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('family:')));
  };

  // Function to copy address from primary member to secondary member
  const copyAddressFromPrimary = () => {
    if (!clubMember.primaryMember) return;

    setClubMember((current) => ({
      ...current,
      secondaryMember: {
        ...(current.secondaryMember as IFamilyMemberDto),
        address: current.primaryMember.address,
        address2: current.primaryMember.address2,
        city: current.primaryMember.city,
        state: current.primaryMember.state,
        zipCode: current.primaryMember.zipCode
      }
    }));

    // Mark the form as dirty
    setIsDirty(true);
  };

  // Open the add child modal
  const openAddChildModal = () => {
    // Reset form data and errors
    setChildFormData({
      firstName: '',
      lastName: '',
      middleName: '',
      dateOfBirthUtc: null
    });
    setChildFormErrors([]);
    setChildFormMode('add');
    setEditingChildId(null);
    setShowAddChildModal(true);
  };

  // Close the add child modal
  const closeAddChildModal = () => {
    setShowAddChildModal(false);
    setChildFormMode('add');
    setEditingChildId(null);
  };

  // Update child form data
  const updateChildFormData = <K extends keyof typeof childFormData>(
    key: K,
    value: (typeof childFormData)[K]
  ) => {
    setChildFormData((prev) => ({
      ...prev,
      [key]: value
    }));

    // Clear error for this field if it exists
    if (childFormErrors.includes(key)) {
      setChildFormErrors((prev) => prev.filter((error) => error !== key));
    }
  };

  // Validate child form data
  const validateChildForm = (): boolean => {
    const errors: string[] = [];

    if (Helpers.isNullOrWhiteSpace(childFormData.firstName)) {
      errors.push('firstName');
    }
    if (Helpers.isNullOrWhiteSpace(childFormData.lastName)) {
      errors.push('lastName');
    }
    if (!childFormData.dateOfBirthUtc) {
      errors.push('dateOfBirthUtc');
    }

    setChildFormErrors(errors);
    return errors.length === 0;
  };

  // Add a child to the family member
  const addChild = async (): Promise<void> => {
    if (addingChild || !clubMember.id || clubMember.id === 'new') return;

    const isValid = validateChildForm();
    if (!isValid) {
      toaster.addToast('Please fill in all required fields', ToastStyle.ERROR, 3000);
      return;
    }

    setAddingChild(true);

    try {
      if (childFormMode === 'add') {
        // Create new child
        const endpoint = `/members/${clubMember.id}/children`;

        // Create payload for API
        const childData = {
          clubMemberId: parseInt(clubMember.id),
          firstName: childFormData.firstName,
          lastName: childFormData.lastName,
          middleName: childFormData.middleName || '',
          dateOfBirthUtc: childFormData.dateOfBirthUtc
        };

        const apiResponse = await api.post<IFamilyMemberChildResult>(
          endpoint,
          JSON.stringify(childData)
        );

        if (apiResponse?.status?.statusCode === 200 || apiResponse?.status?.statusCode === 201) {
          if (apiResponse.response?.succeeded) {
            toaster.addToast('Child added successfully', ToastStyle.SUCCESS, 3000);

            // Refresh the children list
            await fetchFamilyMemberChildren(clubMember.id);

            // Close the modal
            closeAddChildModal();
          } else {
            toaster.addToast(
              `Error adding child: ${apiResponse.response?.message || 'Unknown error'}`,
              ToastStyle.ERROR,
              5000
            );
          }
        } else {
          toaster.addToast(
            `Error adding child: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      } else if (childFormMode === 'edit') {
        // Update existing child
        const endpoint = `/members/${clubMember.id}/children/${editingChildId}`;

        // Create the child DTO for update (similar to how WorkHoursDto is used)
        const childData: IFamilyMemberChildDto = {
          id: editingChildId!,
          clubMemberId: parseInt(clubMember.id),
          firstName: childFormData.firstName,
          lastName: childFormData.lastName,
          middleName: childFormData.middleName || '',
          dateOfBirthUtc: childFormData.dateOfBirthUtc,
          createdBy: '', // Will be set by the server
          dateCreatedUtc: new Date(), // Will be set by the server
          lastModifiedBy: '', // Will be set by the server
          lastModifiedDateUtc: new Date() // Will be set by the server
        };

        const apiResponse = await api.put<IFamilyMemberChildResult>(
          endpoint,
          JSON.stringify(childData)
        );

        if (apiResponse?.status?.statusCode === 200) {
          if (apiResponse.response?.succeeded) {
            toaster.addToast('Child updated successfully', ToastStyle.SUCCESS, 3000);

            // Refresh the children list
            await fetchFamilyMemberChildren(clubMember.id);

            // Close the modal
            closeAddChildModal();
          } else {
            toaster.addToast(
              `Error updating child: ${apiResponse.response?.message || 'Unknown error'}`,
              ToastStyle.ERROR,
              5000
            );
          }
        } else {
          toaster.addToast(
            `Error updating child: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      }
    } catch (error) {
      toaster.addToast(
        `Error ${childFormMode === 'add' ? 'adding' : 'updating'} child`,
        ToastStyle.ERROR,
        5000
      );
    } finally {
      setAddingChild(false);
    }
  };

  // Enable edit mode for a child
  const enableEditChildMode = (child: IFamilyMemberChildDto) => {
    // Populate form with existing data
    setChildFormData({
      firstName: child.firstName,
      lastName: child.lastName,
      middleName: child.middleName,
      dateOfBirthUtc: child.dateOfBirthUtc
    });
    setChildFormMode('edit');
    setEditingChildId(child.id);
    setChildFormErrors([]);
    setShowAddChildModal(true);
  };

  // Show delete confirmation
  const showDeleteChildConfirmationDialog = () => {
    setShowDeleteChildConfirmation(true);
  };

  // Hide delete confirmation
  const hideDeleteChildConfirmationDialog = () => {
    setShowDeleteChildConfirmation(false);
  };

  // Delete a child
  const deleteChild = async (): Promise<void> => {
    if (deletingChild || !clubMember.id || clubMember.id === 'new' || !editingChildId) return;

    setDeletingChild(true);
    setShowDeleteChildConfirmation(false);

    try {
      const endpoint = `/members/${clubMember.id}/children/${editingChildId}`;

      // Create the child DTO for delete (API expects body with child data)
      const childData: IFamilyMemberChildDto = {
        id: editingChildId,
        clubMemberId: parseInt(clubMember.id),
        firstName: childFormData.firstName,
        lastName: childFormData.lastName,
        middleName: childFormData.middleName || '',
        dateOfBirthUtc: childFormData.dateOfBirthUtc,
        createdBy: '', // Will be set by the server
        dateCreatedUtc: new Date(), // Will be set by the server
        lastModifiedBy: '', // Will be set by the server
        lastModifiedDateUtc: new Date() // Will be set by the server
      };

      const apiResponse = await api.sendRequest<IFamilyMemberChildResult>(
        endpoint,
        'DELETE',
        JSON.stringify(childData),
        'application/json'
      );

      if (apiResponse?.status?.statusCode === 200) {
        if (apiResponse.response?.succeeded) {
          toaster.addToast('Child deleted successfully', ToastStyle.SUCCESS, 3000);

          // Refresh the children list
          await fetchFamilyMemberChildren(clubMember.id);

          // Close the modal
          closeAddChildModal();
        } else {
          toaster.addToast(
            `Error deleting child: ${apiResponse.response?.message || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      } else {
        toaster.addToast(
          `Error deleting child: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
          ToastStyle.ERROR,
          5000
        );
      }
    } catch (error) {
      toaster.addToast('Error deleting child', ToastStyle.ERROR, 5000);
    } finally {
      setDeletingChild(false);
    }
  };

  // Handle email reuse confirmation - retry save with confirmation flag
  const confirmEmailReuse = async (): Promise<void> => {
    if (!pendingSecondaryMemberSave && !pendingClubMemberSave && !pendingClubMemberUpdate) return;

    setShowEmailReuseConfirmDialog(false);

    if (pendingSecondaryMemberSave) {
      setSavingSecondaryMember(true);

      try {
        // Retry the save with confirmation flag set
        const updateRequest: ISecondaryFamilyMemberUpdateRequest = {
          ...pendingSecondaryMemberSave,
          confirmReuseExistingAccount: true
        };

        const endpoint = `/members/${clubMember.id}/family`;
        const apiResponse = await api.put<ISecondaryFamilyMemberUpdateResult>(
          endpoint,
          JSON.stringify(updateRequest)
        );

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
          toaster.addToast(
            'Secondary family member saved successfully. Email account transferred.',
            ToastStyle.SUCCESS,
            3000
          );

          // Reset dirty flag and clear pending state
          setIsDirty(false);
          setPendingSecondaryMemberSave(null);
          setEmailConflictInfo(null);

          // Reload the club member to get updated data
          await loadClubMember(clubMember.id);
        } else {
          toaster.addToast(
            `Error updating secondary member: ${apiResponse.response?.message || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      } catch (error) {
        toaster.addToast('Error saving secondary family member', ToastStyle.ERROR, 5000);
      } finally {
        setSavingSecondaryMember(false);
      }
    } else if (pendingClubMemberSave) {
      setIsSaving(true);

      try {
        // Retry the save with confirmation flag set
        const addRequest: IClubMemberAddRequest = {
          ...pendingClubMemberSave,
          confirmReuseExistingAccount: true
        };

        const endpoint = `/members`;
        const apiResponse = await api.post<IClubMemberAddResult>(
          endpoint,
          JSON.stringify(addRequest)
        );

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
          setIsDirty(false);
          setPendingClubMemberSave(null);
          setEmailConflictInfo(null);
          toaster.addToast(
            `Member created successfully! Email account transferred.`,
            ToastStyle.SUCCESS,
            2000
          );
          await navigate(`/members/${apiResponse.response.clubMemberId}`);
        } else {
          toaster.addToast(
            `Error creating member: ${apiResponse.response?.message || 'Unknown error'}`,
            ToastStyle.ERROR,
            8000
          );
        }
      } catch (error) {
        toaster.addToast('Error creating member', ToastStyle.ERROR, 5000);
      } finally {
        setIsSaving(false);
      }
    } else if (pendingClubMemberUpdate) {
      setIsSaving(true);

      try {
        // Retry the update with confirmation flag set
        const updateRequest: IClubMemberDto = {
          ...pendingClubMemberUpdate,
          confirmReuseExistingAccount: true
        };

        const endpoint = `/members/${clubMember.id}`;
        const apiResponse = await api.put<IClubMemberUpdateResult>(
          endpoint,
          JSON.stringify(updateRequest, emptyClubMemberReplacer)
        );

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
          setIsDirty(false);
          setPendingClubMemberUpdate(null);
          setEmailConflictInfo(null);
          toaster.addToast(
            `Member updated successfully! Email account transferred.`,
            ToastStyle.SUCCESS,
            2000
          );

          // Reload the club member to get updated data with proper date processing
          await loadClubMember(clubMember.id);
        } else {
          toaster.addToast(
            `Error updating member: ${apiResponse.response?.message || 'Unknown error'}`,
            ToastStyle.ERROR,
            8000
          );
        }
      } catch (error) {
        toaster.addToast('Error updating member', ToastStyle.ERROR, 5000);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Cancel email reuse confirmation
  const cancelEmailReuse = (): void => {
    setShowEmailReuseConfirmDialog(false);
    setPendingSecondaryMemberSave(null);
    setPendingClubMemberSave(null);
    setPendingClubMemberUpdate(null);
    setEmailConflictInfo(null);
  };

  useEffect(() => {
    void loadClubMember(clubMemberId);
  }, [loadClubMember, clubMemberId]);

  return {
    clubMember,
    isSaving,
    isDirty,
    fieldErrors,
    setFieldErrors,
    loadingClubMember,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    dialogAction,
    setDialogAction,
    targetTab,
    setTargetTab,
    membershipTypes,
    loadingMembershipTypes,
    interests,
    loadingInterests,
    selectedInterests,
    handleInterestChange,
    back,
    updateClubMemberValue,
    updatePrimaryMemberValue,
    saveChanges,
    deleteMember,
    sendFirstTimeEmail,
    isSendingEmail,
    showSetPasswordModal,
    openSetPasswordModal,
    closeSetPasswordModal,
    onPasswordSetSuccess,
    showSecondarySetPasswordModal,
    openSecondarySetPasswordModal,
    closeSecondarySetPasswordModal,
    onSecondaryPasswordSetSuccess,
    showDeleteConfirmationModal,
    openDeleteConfirmationModal,
    closeDeleteConfirmationModal,
    discardUnsavedChanges,
    closeUnsavedChangesDialog,
    cancelChanges,
    ToastContainer: toaster.ToastContainer,
    updateSecondaryMemberValue,
    saveSecondaryFamilyMember,
    savingSecondaryMember,
    memberStatusOptions,
    loadingMemberStatuses,
    cancelSecondaryFamilyMember,
    copyAddressFromPrimary,
    familyMemberChildren,
    loadingChildren,
    showEmailReuseConfirmDialog,
    emailConflictInfo,
    confirmEmailReuse,
    cancelEmailReuse,
    showAddChildModal,
    openAddChildModal,
    closeAddChildModal,
    childFormData,
    childFormErrors,
    updateChildFormData,
    addChild,
    addingChild,
    committees,
    loadingCommittees,
    childFormMode,
    enableEditChildMode,
    showDeleteChildConfirmation,
    showDeleteChildConfirmationDialog,
    hideDeleteChildConfirmationDialog,
    deleteChild,
    deletingChild
  };
};

export default useClubMemberEditor;
